import style from "../style/Personnel.module.scss";
import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import className from "classnames/bind";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {notification, DatePicker, Select} from "antd";
import {
	Loading,
	Button,
	Modal,
	Icon
} from "~/components";
import {
	PersonnelFormGroupSwitch,
	PersonnelFormEdit,
	PersonnelFormPay,
	PersonnelSearchBar,
	PersonnelSearchMobile,
	PersonnelTable,
	PersonnelFormLeader,
	PersonnelFormRole,
	PersonnelCard,
	PersonnelFormPass,
	PersonnelProbationHistory,
	PersonnelHistory
} from "../components";
import {SetCycleModal} from "~/features/EmployeeEvaluation/components";

import {
	personnelErrorSelector,
	personnelItemsSelector,
	personnelLoadingSelector,
	personnelPaginationSelector,
	personnelFilterSelector,
	personnelActions
} from "../personnelSlice";
import {userApi} from "~/api";
import {
	apiError,
	strToTime,
	handleRequest
} from "~/utils";
import {
	useCan,
	useDevice,
	useGroup
} from "~/hooks";

const cn = className.bind(style);

function Personnel() {

	const {isMobile} = useDevice();

	const can = {
	    viewAll: useCan('personnelListAll'),
		viewByDepartment: useCan('personnelListDepartment'),
		viewByGroup: useCan('personnelListGroup'),
		viewByLimit: useCan('personnelListLimit'),
		viewGroup: useCan('groupListAll'),
		viewGroupByManager: useCan('groupListGroup'),
		add: useCan('personnelAdd'),
	}

	const dispatch = useDispatch();

	const items = useSelector(personnelItemsSelector);

	const loading = useSelector(personnelLoadingSelector);

	const error = useSelector(personnelErrorSelector);

	const pagination = useSelector(personnelPaginationSelector);

	const filter = useSelector(personnelFilterSelector);

	//Group items
	const listGroup = useGroup();

	const [itemEdit, setItemEdit] = useState({});

	//Ngày nhân viên nghỉ việc (mặc định là hôm nay)
	const [blockDate, setBlockDate] = useState(dayjs());

	//Người nhận link test khi nhân viên nghỉ việc (để trống = tự động chuyển cho quản lý cấp trên)
	const [receiveUserId, setReceiveUserId] = useState(null);

	//Danh sách nhân viên để chọn người nhận link test
	const [receiveOptions, setReceiveOptions] = useState([]);

	//Modal show
	const [openModal, setOpenModal] = useState({
		edit    : false,
		group   : false,
		pay     : false,
		leader  : false,
		role    : false,
		delete  : false,
		card    : false,
		password: false,
		probationHistory: false,
		history: false,
		setCycle: false,
	});

	const handleModalOpen = (modal) => {
		if(modal === 'delete') {
			setBlockDate(dayjs());
			setReceiveUserId(null);

			//Tải danh sách nhân viên để chọn người nhận link test (chỉ tải một lần)
			if(receiveOptions.length === 0) {
				userApi.all().then(response => {
					const options = (response?.data || []).reduce((list, user) => {
						list.push({
							label: `${user.firstname} ${user.lastname}`.trim() + (user.username ? ` (${user.username})` : ''),
							value: user.id,
						});
						return list;
					}, []);
					setReceiveOptions(options);
				}).catch(() => {});
			}
		}
		openModal[modal] = true;
		setOpenModal({...openModal})
	}

	const handleModalClose = (modal) => {
		openModal[modal] = false;
		setOpenModal({...openModal});
	}

	//Load data
	useEffect(() => {
		handleReLoading()
	}, [filter, can.viewAll, can.viewByDepartment, can.viewByGroup, can.viewByLimit]);

	if (error) {
		notification.error({message: 'Lỗi', description: error});
	}

	const handleReLoading = () => {
		if(can.viewByLimit || can.viewAll || can.viewByDepartment || can.viewByGroup)
		{
			let detail = ['group']

			if(!can.viewByLimit)
			{
				detail.push('papers');
			}
			if(can.add)
			{
				detail.push('working');
			}
			dispatch(personnelActions.fetchData({...filter, detail}));
		}
	}

	//Edit users
	const handleUserEdit = async (data, item) => {
		if (item?.id == 'undefined') {
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		data.birthday = strToTime(data.birthday);
		if (data.contractStart) data.contractStart = strToTime(data.contractStart);
		if (data.contractEnd)   data.contractEnd   = strToTime(data.contractEnd);

		data.id = item.id;

		let [error, response] = await handleRequest(userApi.update(data));

		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);
		if(!message) {
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});
			dispatch(personnelActions.update(response.data));
			handleModalClose('edit');
		}
	}

	//Delete users
	const handleUserDelete = async () => {
		if(typeof itemEdit.id == 'undefined') {
			notification.error({ message: 'Lỗi', description: 'Không xác định được nhân viên cần cho nghỉ việc'});
			return;
		}
		if(itemEdit.status == 'trash') {
			notification.error({ message: 'Lỗi', description: 'Nhân viên này đã nghỉ việc'});
			return;
		}
		if(!blockDate) {
			notification.error({ message: 'Lỗi', description: 'Vui lòng chọn ngày nhân viên nghỉ việc'});
			return;
		}
		const blockParams = { blockTime: strToTime(blockDate) };
		if(receiveUserId) blockParams.receiveUserId = receiveUserId;

		let [error, response] = await handleRequest(userApi.block(itemEdit.id, blockParams));
		let message = apiError(`Cho nhân viên nghỉ việc thất bại`, error, response);
		if(!message) {
			notification.success({message: 'Thành công', description: `Cho nhân viên nghỉ việc thành công`});
			dispatch(personnelActions.delete(itemEdit.id));
			handleModalClose('delete');
		}
	}

	//Group Switch
	const handleUserGroupSwitch = async (data, item) => {
		if (!item?.id)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		if (!data?.group || data.group == 0)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhóm cần chuyển'});
			return;
		}

		if (data.group === item.group)
		{
			notification.error({message: 'Lỗi', description: 'Nhóm của nhân viên không thay đổi'});
			return;
		}

		let [error, response] = await handleRequest(userApi.groupSwitch({ toGroup: data.group, id : item.id }));

		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);

		if(!message) {
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});
			item.groupId = response.data.groupId;
			item.group = response.data.group;
			dispatch(personnelActions.update(item));
			handleModalClose('group');
		}
	}

	//Pay
	const handleUserPay = async (data, item) => {

		if (item?.id == 'undefined') {
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		let userItem = {...data};

		if (userItem.payTime != "") {
			userItem.payTime = Math.round(new Date(userItem.payTime).getTime() / 1000)
		}
		if (userItem.payTypeTime != "") {
			userItem.payTypeTime = Math.round(new Date(userItem.payTypeTime).getTime() / 1000)
		}
		if (userItem.salaryCompaniesTime) {
			userItem.salaryCompaniesTime = Math.round(new Date(userItem.salaryCompaniesTime).getTime() / 1000)
		}

		userItem.id = item.id;

		let [error, response] = await handleRequest(userApi.pay(userItem));
		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);
		if(!message) {
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});
			if(typeof response.data.pay != 'undefined') item.pay = response.data.pay;
			if(typeof response.data.payType != 'undefined') item.payType = response.data.payType;
			if(typeof response.data.payManagerMilestone != 'undefined') item.payManagerMilestone = response.data.payManagerMilestone;
			if(typeof response.data.salaryCompanies != 'undefined') item.salaryCompanies = response.data.salaryCompanies;
			dispatch(personnelActions.update(item));
			handleModalClose('pay');
		}
	}

	//Leader
	const handleUserLeader = async (data, item) => {
		if (item?.id == 'undefined') {
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		let userItem = {...data};

		userItem.id = item.id;

		let [error, response] = await handleRequest(userApi.leader(userItem));
		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);
		if(!message) {
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});
			item.leaderUserList = userItem.users;
			dispatch(personnelActions.update(item));
			handleModalClose('leader');
		}
	}

	//Role
	const handleUserRole = async (data, item) =>
	{
		if (!item?.id)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		if (!data?.role || data.role === 0)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin chức vụ cần chuyển'});
			return;
		}

		if (data.role === item.role.key)
		{
			notification.error({message: 'Lỗi', description: 'Chức vụ của nhân viên không thay đổi'});
			return;
		}

		let [error, response] = await handleRequest(userApi.changeRole({ role: data.role, id: item.id}));

		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);

		if(!message) {
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});
			item.role = response.data.role;
			item.department = response.data.department;
			item.group = response.data.group;
			dispatch(personnelActions.update(item));
			handleModalClose('role');
		}
	}

	const handleLocked = async (item) =>
	{
		if (!item?.id)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		if (item.locked === 1)
		{
			notification.error({message: 'Lỗi', description: 'Nhân viên này hiện đã bị tạm khóa'});
			return;
		}

		let [error, response] = await handleRequest(userApi.locked(item.id));

		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);

		if(!message)
		{
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});

			item.locked = 1;

			dispatch(personnelActions.update(item));
		}
	}

	const handleUnLocked = async (item) =>
	{
		if (!item?.id)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		if (item.locked === 0)
		{
			notification.error({message: 'Lỗi', description: 'Nhân viên này không bị khóa'});
			return;
		}

		let [error, response] = await handleRequest(userApi.unlocked(item.id));

		let message = apiError(`Cập nhật thông tin nhân viên thất bại`, error, response);

		if(!message)
		{
			notification.success({message: 'Thành công', description: `Cập nhật thông tin nhân viên thành công`});

			item.locked = 0;

			dispatch(personnelActions.update(item));
		}
	}

	const handleChangePassword = async (data, item) => {

		if (!item?.id)
		{
			notification.error({message: 'Lỗi', description: 'Không có thông tin nhân viên để cập nhật'});
			return;
		}

		data.id = item.id;

		let [error, response] = await handleRequest(userApi.password(data));

		let message = apiError(`Cập nhật mật khẩu thất bại`, error, response);

		if (!message) {
			notification.success({
				message: 'Thành công', description: `Đổi mật khẩu thành công`
			});
			handleModalClose('password');
		}
	}

	//Search
	const SearchBar = (isMobile) ? PersonnelSearchMobile : PersonnelSearchBar;

	const handlePaginationChange = (page) => {
		dispatch(personnelActions.setFilter({...filter, page }));
	}

	const handleSearchChange = (newFilter) => {
		dispatch(personnelActions.setFilterWithDebounce(newFilter));
	};

	const handleFilterChange = (newFilter) => {
		dispatch(personnelActions.setFilter(newFilter));
	};

	return (
		<>
			<ActionBar title={'Nhân viên'}>
				<Button background blue to="/personnel">{Icon.user}</Button>
				{(can.viewGroup || can.viewGroupByManager) && <Button outline blue to="/personnel/group">{Icon.users}</Button>}
				<Button outline onClick={handleReLoading}>{Icon.reload}</Button>
			</ActionBar>
			<div className={cn('container')}>
				<div className={cn('content')}>
					{loading && <Loading/>}
					<SearchBar listGroup={listGroup} filter={filter} onSearchChange={handleSearchChange} onChange={handleFilterChange} />
					{items && <PersonnelTable
						items={items}
						listGroup={listGroup}
						onDelete={handleUserDelete}
						onPaginationChange={handlePaginationChange}
						setItemEdit={setItemEdit}
						openModal={handleModalOpen}
						pagination={pagination}
						locked={handleLocked}
						unlocked={handleUnLocked}
					/>}
				</div>
				{
					(openModal.edit && itemEdit?.id) && <Modal title="Cập nhật thông tin" visible={openModal.edit} onCancel={() => handleModalClose('edit')}>
						{itemEdit?.id && <PersonnelFormEdit item={itemEdit} onHandleSubmit={handleUserEdit} />}
					</Modal>
				}
				{
					(openModal.pay && itemEdit?.id) && <Modal title="Lương" visible={openModal.pay} onCancel={() => handleModalClose('pay')}>
						<PersonnelFormPay item={itemEdit} onHandleSubmit={handleUserPay} />
					</Modal>
				}
				{
					(openModal.group && itemEdit?.id) && <Modal title="Chuyển nhóm" visible={openModal.group} onCancel={() => handleModalClose('group')}>
						<PersonnelFormGroupSwitch item={itemEdit} onHandleSubmit={handleUserGroupSwitch} listGroup={listGroup} />
					</Modal>
				}

				{
					(openModal.role && itemEdit?.id) && <Modal title="Chức vụ" visible={openModal.role} onCancel={() => handleModalClose('role')}>
						<PersonnelFormRole item={itemEdit} onHandleSubmit={handleUserRole} />
					</Modal>
				}
				{
					(openModal.leader && itemEdit?.id) && <Modal title="Nhóm trưởng" visible={openModal.leader} onCancel={() => handleModalClose('leader')}>
						<PersonnelFormLeader item={itemEdit} onHandleSubmit={handleUserLeader} />
					</Modal>
				}
				{
					(openModal.delete && itemEdit?.id) && <Modal title="Cho nhân viên nghỉ việc" visible={openModal.delete} onCancel={() => handleModalClose('delete')}>
						<p>
							Bạn muốn cho nhân viên <b>{itemEdit.firstname+' '+itemEdit.lastname}</b> nghỉ việc ?
						</p>
						<div className="form-group mt-1">
							<label htmlFor="blockDate">Ngày nghỉ việc</label>
							<DatePicker
								id="blockDate"
								className="form-control mt-1"
								style={{width: '100%'}}
								format="DD/MM/YYYY"
								value={blockDate}
								onChange={(date) => setBlockDate(date)}
								placeholder="Chọn ngày nghỉ việc"
								popupStyle={{zIndex: 10000}}
							/>
						</div>
						<div className="form-group mt-1">
							<label htmlFor="receiveUser">Người nhận link test</label>
							<Select
								id="receiveUser"
								className="mt-1 mb-1"
								style={{width: '100%'}}
								allowClear
								showSearch
								optionFilterProp="label"
								value={receiveUserId}
								onChange={(value) => setReceiveUserId(value ?? null)}
								placeholder="Để trống để tự động chuyển cho quản lý cấp trên"
								options={receiveOptions.filter((option) => option.value !== itemEdit.id)}
								popupStyle={{zIndex: 10000}}
							/>
							<small className="text-muted">Nếu để trống, toàn bộ link test sẽ được chuyển cho nhóm trưởng / quản lý cấp trên của nhân viên.</small>
						</div>
						<div className="d-flex justify-content-end gap modal-bottom pd-1">
							<Button white leftIcon={Icon.close} onClick={() => handleModalClose('delete')}> Đóng </Button>
							<Button primary leftIcon={Icon.delete} onClick={handleUserDelete}> Đồng ý </Button>
						</div>
					</Modal>
				}
				{
					(openModal.card && itemEdit?.id) && <Modal title="Thẻ nhân viên" visible={openModal.card} onCancel={() => handleModalClose('card')}>
						<PersonnelCard item={itemEdit} />
					</Modal>
				}
				{
					(openModal.password && itemEdit?.id) && <Modal title="Đổi mật khẩu" visible={openModal.password} onCancel={() => handleModalClose('password')}>
						<PersonnelFormPass item={itemEdit} onHandleSubmit={handleChangePassword} />
					</Modal>
				}
				{
					(openModal.probationHistory && itemEdit?.id) && <Modal size="lg" title={`Lịch sử thực tập - thử việc: ${itemEdit.firstname} ${itemEdit.lastname}`} visible={openModal.probationHistory} onCancel={() => handleModalClose('probationHistory')}>
						<PersonnelProbationHistory item={itemEdit} />
					</Modal>
				}
				{
					(openModal.history && itemEdit?.id) && <Modal size="lg" title={`Lịch sử thao tác: ${itemEdit.firstname} ${itemEdit.lastname}`} visible={openModal.history} onCancel={() => handleModalClose('history')}>
						<PersonnelHistory item={itemEdit} />
					</Modal>
				}
				{
					(openModal.setCycle && itemEdit?.id) && <Modal title="Thiết lập chu kỳ đánh giá" visible={openModal.setCycle} onCancel={() => handleModalClose('setCycle')}>
						<SetCycleModal item={itemEdit} onSuccess={() => handleModalClose('setCycle')} />
					</Modal>
				}
			</div>
		</>
	)
}

export default Personnel;